import { useState, useRef, useCallback, useMemo } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { PettableNPC as PettableNPCData, WorldType } from '@hollowveil/shared';
import { useGameStore } from '@hollowveil/shared';
import { getNPCModelComponent } from './npcs';

interface PettableNPCProps {
  npc: PettableNPCData;
  world: WorldType;
}

// Heart particles for when animal is petted
interface HeartParticle {
  id: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  scale: number;
  opacity: number;
  rotation: number;
}

// Create heart shape geometry
function createHeartGeometry(): THREE.ShapeGeometry {
  const shape = new THREE.Shape();
  const x = 0, y = 0;

  // Heart shape using bezier curves
  shape.moveTo(x, y + 0.25);
  shape.bezierCurveTo(x, y + 0.25, x - 0.25, y, x - 0.25, y);
  shape.bezierCurveTo(x - 0.55, y, x - 0.55, y + 0.35, x - 0.55, y + 0.35);
  shape.bezierCurveTo(x - 0.55, y + 0.55, x - 0.35, y + 0.77, x, y + 1);
  shape.bezierCurveTo(x + 0.35, y + 0.77, x + 0.55, y + 0.55, x + 0.55, y + 0.35);
  shape.bezierCurveTo(x + 0.55, y + 0.35, x + 0.55, y, x + 0.25, y);
  shape.bezierCurveTo(x + 0.25, y, x, y + 0.25, x, y + 0.25);

  const geometry = new THREE.ShapeGeometry(shape);
  geometry.center();
  geometry.scale(0.15, 0.15, 0.15); // Scale down to appropriate size
  return geometry;
}

// Singleton heart geometry (reused for all hearts)
const heartGeometry = createHeartGeometry();

export function PettableNPC({ npc, world }: PettableNPCProps) {
  const { player, interactWithObject } = useGameStore();
  const [hovered, setHovered] = useState(false);
  const [isPetting, setIsPetting] = useState(false);
  const [canPet, setCanPet] = useState(true);
  const [hearts, setHearts] = useState<HeartParticle[]>([]);
  const groupRef = useRef<THREE.Group>(null);
  const animationTimeRef = useRef(0);
  const lastPetTimeRef = useRef(0);

  // Check visibility based on world
  const isVisible = npc.visibleInWorld === 'both' || npc.visibleInWorld === world;

  // Calculate distance to player
  const distanceToPlayer = useMemo(() => {
    const dx = player.position[0] - npc.position[0];
    const dz = player.position[2] - npc.position[2];
    return Math.sqrt(dx * dx + dz * dz);
  }, [player.position, npc.position]);

  const isInRange = distanceToPlayer <= npc.petRadius;

  // Handle petting interaction
  const handlePet = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();

    if (!npc.isPettable || !canPet || !isInRange) return;

    const now = Date.now();
    if (now - lastPetTimeRef.current < npc.petCooldown) return;

    lastPetTimeRef.current = now;
    setIsPetting(true);
    setCanPet(false);

    // Spawn heart particles
    const newHearts: HeartParticle[] = [];
    for (let i = 0; i < 5; i++) {
      newHearts.push({
        id: Date.now() + i,
        position: new THREE.Vector3(
          (Math.random() - 0.5) * 0.3,
          0.5 + Math.random() * 0.3,
          (Math.random() - 0.5) * 0.3
        ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.5,
          1 + Math.random() * 0.5,
          (Math.random() - 0.5) * 0.5
        ),
        scale: 0.08 + Math.random() * 0.04,
        opacity: 1,
        rotation: Math.random() * Math.PI,
      });
    }
    setHearts(newHearts);

    // Trigger quest objective completion
    interactWithObject(npc.id);

    // Reset petting state after animation
    setTimeout(() => {
      setIsPetting(false);
    }, 800);

    // Reset can pet after cooldown
    setTimeout(() => {
      setCanPet(true);
    }, npc.petCooldown);
  }, [npc.isPettable, npc.petCooldown, npc.id, canPet, isInRange, interactWithObject]);

  // Animation frame
  useFrame((_, delta) => {
    animationTimeRef.current += delta;
    const time = animationTimeRef.current;

    if (groupRef.current) {
      // Idle animations based on type
      if (npc.idleAnimation === 'sit') {
        // Gentle breathing motion
        groupRef.current.scale.y = (npc.scale || 1) * (1 + Math.sin(time * 2) * 0.02);
      } else if (npc.idleAnimation === 'fly') {
        // Hovering motion
        groupRef.current.position.y = npc.position[1] + Math.sin(time * 3) * 0.1;
      } else if (npc.idleAnimation === 'swim') {
        // Swimming motion
        groupRef.current.position.y = npc.position[1] + Math.sin(time * 2) * 0.05;
        groupRef.current.rotation.y = (npc.rotation?.[1] || 0) + Math.sin(time) * 0.1;
      } else if (npc.idleAnimation === 'graze') {
        // Head bobbing
        groupRef.current.rotation.x = Math.sin(time * 1.5) * 0.1;
      }

      // Pet animation - happy bounce
      if (isPetting) {
        const bounce = Math.sin(time * 15) * 0.1;
        groupRef.current.position.y = npc.position[1] + Math.abs(bounce);
        groupRef.current.rotation.z = Math.sin(time * 12) * 0.1;
      }
    }

    // Update heart particles
    if (hearts.length > 0) {
      setHearts(prevHearts =>
        prevHearts
          .map(heart => ({
            ...heart,
            position: new THREE.Vector3(
              heart.position.x + heart.velocity.x * delta,
              heart.position.y + heart.velocity.y * delta,
              heart.position.z + heart.velocity.z * delta
            ),
            velocity: new THREE.Vector3(
              heart.velocity.x * 0.98,
              heart.velocity.y - delta * 2,
              heart.velocity.z * 0.98
            ),
            opacity: heart.opacity - delta * 0.8,
            rotation: heart.rotation + delta * 2,
          }))
          .filter(heart => heart.opacity > 0)
      );
    }
  });

  if (!isVisible) return null;

  // Calculate hitbox size based on scale - minimum 1.5 units for easy clicking
  const hitboxSize = Math.max(1.5, (npc.scale || 1) * 1.2);

  return (
    <group
      ref={groupRef}
      position={npc.position}
      rotation={npc.rotation || [0, 0, 0]}
      scale={npc.scale || 1}
    >
      {/* INVISIBLE HITBOX - Large clickable area for easier interaction */}
      <mesh
        onClick={handlePet}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        position={[0, hitboxSize / 2, 0]}
      >
        <boxGeometry args={[hitboxSize, hitboxSize, hitboxSize]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Animal model (visual only, clicks handled by hitbox above) */}
      <group>
        <AnimalModel type={npc.type} world={world} hovered={hovered && isInRange && canPet} />
      </group>

      {/* Pet indicator when in range - larger and more visible */}
      {isInRange && canPet && npc.isPettable && (
        <group position={[0, 1.5, 0]}>
          {/* Paw icon indicator - bigger */}
          <mesh>
            <circleGeometry args={[0.25, 16]} />
            <meshBasicMaterial color="#FFD700" transparent opacity={0.8 + Math.sin(animationTimeRef.current * 4) * 0.2} />
          </mesh>
          <mesh position={[0, 0, 0.01]}>
            <circleGeometry args={[0.12, 8]} />
            <meshBasicMaterial color="#FFA500" />
          </mesh>
        </group>
      )}

      {/* Always show a small indicator that this NPC is pettable */}
      {npc.isPettable && !isInRange && (
        <group position={[0, 1.2, 0]}>
          <mesh>
            <circleGeometry args={[0.1, 12]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.4} />
          </mesh>
        </group>
      )}

      {/* Heart particles */}
      {hearts.map(heart => (
        <group key={heart.id} position={heart.position}>
          <mesh
            rotation={[0, 0, heart.rotation]}
            scale={[heart.scale * 8, heart.scale * 8, 1]}
            geometry={heartGeometry}
          >
            <meshBasicMaterial
              color="#FF1493"
              transparent
              opacity={heart.opacity}
              side={THREE.DoubleSide}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Animal model renderer
interface AnimalModelProps {
  type: string;
  world: WorldType;
  hovered: boolean;
}

function AnimalModel({ type, world, hovered }: AnimalModelProps) {
  // Check if NPC model exists in the modular registry first
  const RegistryModel = getNPCModelComponent(type);
  if (RegistryModel) {
    return <RegistryModel world={world} hovered={hovered} />;
  }

  // Fallback for unknown NPC types - all NPCs should be in the registry
  return (
    <group>
      {/* Default placeholder for unknown NPC */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <capsuleGeometry args={[0.15, 0.3, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a3a4a'}
          emissive={hovered ? '#FFD700' : undefined}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.55, 0.05]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#A0522D' : '#6a4a5a'} />
      </mesh>
      {/* Eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.58, 0.12]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
    </group>
  );
}
